/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ComponentType, useCallback, useMemo, useRef } from 'react';
import Paper from '@mui/material/Paper';
import {
  Grid,
  TableColumnVisibility,
  Table as DevExpressTable,
  TableHeaderRow,
  PagingPanel,
  ColumnChooser,
  SearchPanel,
  Toolbar,
  TableEditRow,
  TableEditColumn,
  TableFilterRow,
  TableGroupRow,
  TableColumnResizing,
  ExportPanel,
} from '@devexpress/dx-react-grid-material-ui';
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  SearchState,
  Sorting,
  EditingState,
  ChangeSet,
  GroupingState,
  IntegratedGrouping,
  CustomPaging,
  TableFilterRow as TableFilterRowBase,
} from '@devexpress/dx-react-grid';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import { Box, Button, IconButton } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AddButton from '../../pages/Options/BrachofficePage/components/AddButton';
import EditButton from '../../pages/Options/BrachofficePage/components/EditButton';
import DeleteButton from '../../pages/Options/BrachofficePage/components/DeleteButton';
import CommitButton from '../../pages/Options/BrachofficePage/components/CommitButton';
import { Service } from '../../types/Service.types';
import RowsProvider from '../../pages/AgentTable/components/RowsProvider';
import { DEFAULTTABLEPAGESIZE } from '../../services/config/CONST';
import {
  TABLE_COMMAND_ACTIONS,
  tableCommandMarker,
  type UsageTableCommandId,
  type UsageTableName,
} from '../../features/usage-tracking/usageKeys';
import { createTableUsageReporter } from '../../features/usage-tracking/services/tableUsage';
import { usageTracker } from '../../features/usage-tracking/services/tracker';

function ToolbarRoot({ ...restProps }) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Toolbar.Root {...restProps} />;
}

interface CancelButtonProps {
  onExecute: () => void;
  /** Marker der Nutzungserfassung (RITA-1504); der Wrapper bildet ihn aus dem Tabellennamen. */
  usageKey?: string;
}
function CancelButton({ onExecute, usageKey }: CancelButtonProps) {
  return (
    <IconButton color="secondary" onClick={onExecute} title="Änderungen verwerfen" data-usage={usageKey}>
      <CancelIcon />
    </IconButton>
  );
}

// TODO What the code in the commend do

// const global = { title: ['Yes', 'No'] };

// const availableValues = {
//   is_default: global.title,
//   show_in_export_xml: global.title,
// };

export const filterRowMessages: TableFilterRowBase.LocalizationMessages = {
  filterPlaceholder: 'Filter...',
  contains: 'Enthält',
  notContains: 'Enthält nicht',
  startsWith: 'Beginnt mit',
  endsWith: 'Endet mit',
  equal: 'Gleich',
  notEqual: 'Ungleich',
  greaterThan: 'Größer als',
  greaterThanOrEqual: 'Größer oder gleich',
  lessThan: 'Kleiner als',
  lessThanOrEqual: 'Kleiner oder gleich',
};

function EditCell(props: TableEditRow.CellProps) {
  // const { column, value, editingEnabled, row } = props;
  // TODO What the code in the commend do

  // @ts-ignore
  // const availableColumnValues = availableValues[column?.name];
  // if (availableColumnValues) {
  //   return <LookupEditCell {...props} availableColumnValues={availableColumnValues} />;
  // }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TableEditRow.Cell {...props} />;
}

// CONTRACT: Jede row MUSS ein valides id-Feld haben (string|number, kein undefined/NaN).
// CONTRACT: Platzhalter-Rows (z.B. PrmUserInit) verwenden id = 0 als Sentinel.
// CONTRACT: Der ?? 0-Fallback ist letzte Verteidigung gegen fehlende id-Felder — strukturelle Garantie ist PrmUserInit.id = 0.
// CONTRACT: Exportiert fuer Unit-/E2E-Tests — Aenderungen hier brechen Vertrags-Assertions in user-password-hash-removal.spec.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRowId = (row: any): string | number => row.id ?? 0;

/** Filterzeile des Grids. Ausgewertet wird nur die Spalte; der Wert bleibt im Wrapper. */
export interface TableFilterLike {
  columnName?: string;
  value?: unknown;
  operation?: string;
}

/**
 * Spalten, deren Filter sich geändert hat — gesetzt, geändert oder entfernt (RITA-1504).
 *
 * Das Grid meldet bei jeder Änderung die GANZE Filterliste. Ohne Vergleich mit dem vorherigen
 * Stand zählte jeder Tastendruck in einer Spalte auch für alle übrigen gesetzten Filter. Der
 * Vergleichswert wird nur hier gebildet: zurück kommen ausschließlich Spaltennamen, Filterwerte
 * verlassen diese Funktion nie.
 */
export const changedFilterColumns = (
  previous: readonly TableFilterLike[],
  next: readonly TableFilterLike[],
): string[] => {
  const fingerprints = (filters: readonly TableFilterLike[]): Map<string, string> => {
    const map = new Map<string, string>();
    filters.forEach((filter) => {
      if (!filter || typeof filter.columnName !== 'string') return;
      map.set(filter.columnName, JSON.stringify([filter.operation ?? '', String(filter.value ?? '')]));
    });
    return map;
  };
  const before = fingerprints(previous);
  const after = fingerprints(next);
  const changed: string[] = [];
  after.forEach((fingerprint, columnName) => {
    if (before.get(columnName) !== fingerprint) changed.push(columnName);
  });
  before.forEach((fingerprint, columnName) => {
    if (!after.has(columnName)) changed.push(columnName);
  });
  return changed;
};

interface Props {
  /**
   * Name dieser Tabelle in der Nutzungserfassung (RITA-1504). Pflicht, damit kein
   * Tabellen-Vorgang ohne Namen gemeldet wird und die Auswertung je Tabelle eine feste,
   * schreibweisensichere Zeile bekommt. Erlaubte Werte stehen in usageKeys.ts (TABLE_NAMES).
   */
  usageName: UsageTableName;
  row: any[];
  columns: any[];
  pageSize?: number;
  pageSizes?: number[];
  defaultHiddenColumnNames?: string[];
  defaultColumnWidths?: any[];
  onCommitChanges?: (changes: ChangeSet) => void;
  columnExtensions?: EditingState.ColumnExtension[];
  dataTypeProvider?: (() => JSX.Element)[];
  // TODO implement commandComponent to Table
  // commandComponent?: ComponentType<TableEditColumn.CommandProps>;
  EditCellForCommand?: ComponentType<TableEditColumn.CellProps>;
  rowComponent?: ComponentType<DevExpressTable.DataRowProps>;
  FilterRow?: boolean;
  editable?: boolean;
  showAddCommand?: boolean;
  showEditCommand?: boolean;
  showDeleteCommand?: boolean;
  groupingTable?: boolean;
  groupingCollumName?: any[];
  filteringState?: any[];
  sortingProp?: any[];
  forServicesById?: { tableName: string; services: Service[]; agent_id: string | undefined; refresh: () => void };
  exporting?: {
    all?: {
      export: boolean;
      refConst: any | null;
      rows: any[];
      columns: any[];
      onSave: (wokbook: any) => void;
      exportFunk: () => any;
    };
    filter?: {
      export: boolean;
      refConst: any | null;
      columns: any[];
      onSave: (wokbook: any) => void;
      exportFunk: () => any;
    };
  };
  TableCommandComponents?: any;
  // Server-side props
  totalCount?: number;
  currentPage?: number;
  onCurrentPageChange?: (page: number) => void;
  externalSorting?: Sorting[];
  onSortingChange?: (sorting: Sorting[]) => void;
  externalFiltering?: any[];
  onFiltersChange?: (filters: any[]) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  FilterCell?: ComponentType<TableFilterRow.CellProps>;
}

export const CommandComponents = {
  add: AddButton,
  edit: EditButton,
  delete: DeleteButton,
  commit: CommitButton,
  cancel: CancelButton,
};

function Table({
  usageName,
  row,
  columns,
  pageSize = DEFAULTTABLEPAGESIZE.pageSize,
  pageSizes = [5, 10, 15, 20, 0],
  defaultHiddenColumnNames = [],
  defaultColumnWidths = [],
  onCommitChanges,
  columnExtensions,
  dataTypeProvider = [],
  FilterRow = false,
  editable = false,
  showAddCommand = false,
  showEditCommand = false,
  showDeleteCommand = false,
  EditCellForCommand,
  groupingTable = false,
  groupingCollumName,
  forServicesById,
  filteringState = [],
  sortingProp,
  rowComponent,
  exporting,
  TableCommandComponents,
  totalCount,
  currentPage,
  onCurrentPageChange,
  externalSorting,
  onSortingChange,
  externalFiltering,
  onFiltersChange,
  searchValue,
  onSearchChange,
  FilterCell,
}: Props): JSX.Element {
  // TODO implement sorting to Table
  const [internalSorting, setInternalSorting] = useState<Sorting[]>([{ columnName: 'ID', direction: 'asc' }]);
  const [internalCurrentPage, setInternalCurrentPage] = useState<number>(0);
  const [pageSizeState, setPageSize] = useState<number>(pageSize);
  const [exportedRows, setExportedRows] = useState([]);

  useEffect(() => {
    setPageSize(pageSize);
  }, [pageSize]);

  // Nutzungserfassung (RITA-1504): ein Melder je Tabelleninstanz. Er hängt an den
  // Plugin-Rückrufen — nur echte Bedienung meldet, die Montage und der interne Abgleich der
  // Seitengröße (useEffect oben) bleiben stumm. dispose() schickt einen ausstehenden
  // Filtervorgang beim Abbau der Tabelle noch ab.
  const usageReporter = useMemo(
    () => createTableUsageReporter(usageTracker.record, usageName, { getRoute: usageTracker.getRoute }),
    [usageName],
  );
  useEffect(() => () => usageReporter.dispose(), [usageReporter]);
  // Vergleichsstand der Filter (siehe changedFilterColumns); Startwert sind die Vorgabefilter.
  const lastFiltersRef = useRef<TableFilterLike[]>(externalFiltering || filteringState);

  // Alle Rückrufe an die Grid-Plugins sind mit useCallback stabil. Der Plugin-Host des Grids
  // wertet jede neue Prop-Identität als Änderung und rechnet die Getter neu; RowsProvider setzt
  // daraufhin State in diesem Wrapper. Mit je Darstellung neuen Handlern entstünde daraus eine
  // Endlosschleife (Sonde RITA-1504: ~200 Darstellungen je Sekunde auf jeder Seite mit Tabelle).

  const handleSorting = useCallback(
    (newSorting: Sorting[]) => {
      usageReporter.sort(newSorting || []);
      if (onSortingChange) {
        onSortingChange(newSorting);
      } else {
        setInternalSorting(newSorting);
      }
    },
    [usageReporter, onSortingChange],
  );

  const handleFiltersChange = useCallback(
    (filters: TableFilterLike[]) => {
      const nextFilters = filters || [];
      usageReporter.filter(changedFilterColumns(lastFiltersRef.current, nextFilters));
      lastFiltersRef.current = nextFilters;
      if (onFiltersChange) {
        onFiltersChange(filters);
      }
    },
    [usageReporter, onFiltersChange],
  );

  const handleSearchValueChange = useCallback(
    (value: string) => {
      usageReporter.search(typeof value === 'string' ? value.length : 0);
      if (onSearchChange) {
        onSearchChange(value);
      }
    },
    [usageReporter, onSearchChange],
  );

  const finalSorting = externalSorting || internalSorting;
  const finalCurrentPage = currentPage !== undefined ? currentPage : internalCurrentPage;
  const finalOnCurrentPageChange = onCurrentPageChange || setInternalCurrentPage;

  const handleCurrentPageChange = useCallback(
    (page: number) => {
      usageReporter.page(page);
      finalOnCurrentPageChange(page);
    },
    [usageReporter, finalOnCurrentPageChange],
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      usageReporter.pageSize(nextPageSize);
      setPageSize(nextPageSize);
    },
    [usageReporter],
  );
  // const finalFiltering = externalFiltering || filteringState;

  // Use Integrated plugins only if totalCount is NOT provided (Client-Side Mode)
  const isServerSide = totalCount !== undefined;

  // Aufrufer-Angaben über eine Ref, damit Command seine Identität behält: eine neue
  // Kommando-Komponente würde die Zelle neu montieren und einen offenen Dialog (z. B. "neue
  // Leistung hinzufügen") schließen. Über die Ref bleiben die Werte trotzdem aktuell.
  const commandPropsRef = useRef({ TableCommandComponents, forServicesById });
  commandPropsRef.current = { TableCommandComponents, forServicesById };

  const Command = useCallback(
    ({ id, onExecute }: TableEditColumn.CommandProps) => {
      const { TableCommandComponents: eigeneKommandos, forServicesById: leistungen } = commandPropsRef.current;
      const CommandButton = eigeneKommandos ? eigeneKommandos[id] : CommandComponents[id];
      // Nutzungserfassung (RITA-1504): Marker aus Tabellenname und Aktion; unbekannte Kommandos
      // bleiben unmarkiert, damit kein erfundener Name in die Auswertung gerät.
      const usageKey =
        id in TABLE_COMMAND_ACTIONS ? tableCommandMarker(usageName, id as UsageTableCommandId) : undefined;
      return <CommandButton onExecute={onExecute} forServicesById={leistungen} usageKey={usageKey} />;
    },
    [usageName],
  );

  const DefaultRow = useCallback(
    ({ row: dataRow, children, tableRow }: DevExpressTable.DataRowProps) => (
      <DevExpressTable.Row
        key={Math.random()}
        tableRow={tableRow}
        row={dataRow}
        // eslint-disable-next-line react/no-children-prop
        children={children}
      />
    ),
    [],
  );

  const exportMenu = useCallback(
    () => (
      <Box display="flex" flexDirection="column">
        {exporting?.all?.export && (
          <Button
            onClick={() => {
              // Gezählt wird der gedrückte Knopf, nicht der Ausgang des Exports.
              usageReporter.exportStarted('alle');
              exporting?.all?.exportFunk?.();
            }}
          >
            Alle
          </Button>
        )}
        {exporting?.filter?.export && (
          <Button
            onClick={() => {
              usageReporter.exportStarted('filter');
              exporting?.filter?.exportFunk?.();
            }}
          >
            Filter
          </Button>
        )}
      </Box>
    ),
    [],
  );
  const [columnWidths, setColumnWidths] = useState(
    defaultColumnWidths.length > 0 ? defaultColumnWidths : columns.map((col) => ({ columnName: col.name, width: 180 })),
  );
  const [resizingMode] = useState('nextColumn');
  const [hiddenColumnNames, setHiddenColumnNames] = useState<string[]>([]);

  const handleHiddenColumnNamesChange = useCallback(
    (nextHiddenColumnNames: string[]) => {
      usageReporter.columns(nextHiddenColumnNames ? nextHiddenColumnNames.length : 0);
      setHiddenColumnNames(nextHiddenColumnNames);
    },
    [usageReporter],
  );

  // Memoize visibleColumns to prevent unnecessary re-renders
  const visibleColumns = useMemo(
    () =>
      /* @ts-ignore */
      columns.filter((column) => !hiddenColumnNames.includes(column.name)),
    [columns, hiddenColumnNames],
  );

  return (
    // For the correct display of the tables, the position of the table extensions must be observed
    <Paper>
      <Grid rows={row} columns={columns} getRowId={getRowId}>
        <SearchState defaultValue="" value={searchValue} onValueChange={handleSearchValueChange} />
        <FilteringState
          defaultFilters={filteringState}
          filters={externalFiltering}
          onFiltersChange={handleFiltersChange}
        />
        <SortingState
          defaultSorting={sortingProp || [{ columnName: 'exported_at', direction: 'desc' }]}
          sorting={finalSorting}
          onSortingChange={handleSorting}
        />
        {groupingTable && <GroupingState grouping={groupingCollumName} />}
        {groupingTable && <IntegratedGrouping />}
        <PagingState
          currentPage={finalCurrentPage}
          onCurrentPageChange={handleCurrentPageChange}
          pageSize={pageSizeState}
          onPageSizeChange={handlePageSizeChange}
        />
        {!isServerSide && <IntegratedFiltering />}
        {!isServerSide && <IntegratedSorting />}
        {isServerSide ? <CustomPaging totalCount={totalCount} /> : <IntegratedPaging />}
        <RowsProvider onRowsUpdate={setExportedRows} />
        <EditingState onCommitChanges={onCommitChanges || (() => {})} columnExtensions={columnExtensions} />
        <DevExpressTable rowComponent={rowComponent || DefaultRow} messages={{ noData: 'Keine Daten' }} />
        <TableColumnResizing
          columnWidths={columnWidths}
          onColumnWidthsChange={setColumnWidths}
          resizingMode={resizingMode}
        />

        <TableHeaderRow showSortingControls messages={{ sortingHint: 'Sortieren' }} />
        <TableColumnVisibility
          defaultHiddenColumnNames={defaultHiddenColumnNames}
          hiddenColumnNames={hiddenColumnNames}
          onHiddenColumnNamesChange={handleHiddenColumnNamesChange}
        />
        {dataTypeProvider.map((item) => item())}
        {FilterRow && (
          <TableFilterRow messages={filterRowMessages} {...(FilterCell ? { cellComponent: FilterCell } : {})} />
        )}
        <Toolbar rootComponent={ToolbarRoot} />
        <SearchPanel messages={{ searchPlaceholder: 'Suchen...' }} />
        <ColumnChooser messages={{ showColumnChooser: 'Spaltenauswahl anzeigen' }} />
        <PagingPanel
          pageSizes={pageSizes}
          messages={{
            showAll: 'Alle',
            rowsPerPage: 'Zeilen pro Seite',
            info: ({ from, to, count }) => `${from}–${to} von ${count}`,
          }}
        />
        {editable && <TableEditRow cellComponent={EditCell} />}
        {editable && (
          <TableEditColumn
            // eslint-disable-next-line react/jsx-props-no-spreading
            cellComponent={EditCellForCommand || TableEditColumn.Cell}
            showAddCommand={showAddCommand}
            showEditCommand={showEditCommand}
            showDeleteCommand={showDeleteCommand}
            commandComponent={Command}
          />
        )}
        {groupingTable && <TableGroupRow />}
        {/* {exporting?.filter?.export && <ExportPanel menuItemComponent={exportMenu} />} */}
        {exporting?.all?.export && <ExportPanel menuItemComponent={exportMenu} />}
      </Grid>
      {exporting?.all?.export && (
        <GridExporter
          ref={exporting.all.refConst}
          rows={exporting.all.rows}
          columns={visibleColumns}
          onSave={exporting.all.onSave}
        />
      )}
      {exporting?.filter?.export && (
        <GridExporter
          ref={exporting.filter.refConst}
          rows={exportedRows}
          columns={visibleColumns}
          onSave={exporting.filter.onSave}
        />
      )}
    </Paper>
  );
}

export default Table;
